import React, { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash.debounce';
import { connect } from 'react-redux';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';
import { getAllBrands as getAllBrandsAction } from '../../ReduxToolkit/Reducers/brands';
import { getAllRetailers as getAllRetailersAction } from '../../ReduxToolkit/Reducers/retailers';
import { addSalesAgentProfile as addSalesAgentProfileAction } from '../../ReduxToolkit/Reducers/organizationManagement';

const TAKE = 40;

const AddSalesAgentProfilesModal = ({
  open,
  onClose,
  organizationId,
  profileType,
  onSuccess,
  getAllBrands,
  getAllRetailers,
  addSalesAgentProfile,
  totalBrands,
  totalRetailers,
}) => {
  const [searchTxt, setSearchTxt] = useState('');
  const [items, setItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const total = profileType === 'brand' ? totalBrands : totalRetailers;
  const entityLabel = profileType === 'brand' ? 'Brands' : 'Retailers';

  const fetchData = async (_page = 1, _searchTxt = '', append = false) => {
    if (append) {
      setLoadMoreLoading(true);
    } else {
      setLoading(true);
    }

    try {
      const data =
        profileType === 'brand'
          ? await getAllBrands(_page, {}, _searchTxt, TAKE)
          : await getAllRetailers(_page, {}, _searchTxt, TAKE);

      setItems((prev) => (append ? [...prev, ...(data || [])] : data || []));
    } finally {
      setLoading(false);
      setLoadMoreLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setPage(1);
      fetchData(1, value, false);
    }, 500),
    [profileType]
  );

  useEffect(() => {
    if (!open) return;

    setSearchTxt('');
    setSelectedIds([]);
    setPage(1);
    setItems([]);
    fetchData(1, '', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, profileType]);

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchTxt(value);
    debouncedSearch(value);
  };

  const handleLoadMore = async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    await fetchData(nextPage, searchTxt, true);
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (!selectedIds.length) return;

    setSubmitLoading(true);
    try {
      const result = await addSalesAgentProfile(organizationId, selectedIds, profileType);
      if (result?.statusCode === 200) {
        onClose();
        onSuccess();
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      toggle={onClose}
      style={{ maxWidth: '820px' }}
      className="p-3 m-0 m-md-auto modal-custom-width"
    >
      <ModalHeader toggle={onClose}>
        <span style={{ fontSize: '24px', fontWeight: '700', textTransform: 'uppercase' }}>
          Add {entityLabel}
        </span>
      </ModalHeader>

      <ModalBody style={{ padding: '1rem 2rem', maxHeight: '80vh', overflow: 'auto' }}>
        <Input
          type="text"
          placeholder={`Search ${entityLabel.toLowerCase()} by name or website`}
          value={searchTxt}
          onChange={handleSearchChange}
          className="mb-3"
        />

        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
            <Spinner />
          </div>
        ) : items.length > 0 ? (
          <>
            {items.map((item) => (
              <div
                key={item.id}
                className="mb-3"
                style={{ padding: '10px 0', borderBottom: '1px solid #e9ecef' }}
              >
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <div style={{ flex: 1 }}>
                    <p
                      style={{
                        fontSize: '14px',
                        color: '#212529',
                        fontWeight: 600,
                        marginBottom: '4px',
                      }}
                    >
                      {item?.name || '--'}
                    </p>
                    <small className="text-muted d-block">ID: {item?.id}</small>
                    <small className="text-muted d-block text-truncate" title={item?.website || ''}>
                      {item?.website || '--'}
                    </small>
                    {item?.country && (
                      <small className="text-muted d-block">{item.country}</small>
                    )}
                  </div>
                  <Input
                    type="checkbox"
                    checked={selectedIds.includes(item.id)}
                    onChange={() => handleToggleSelect(item.id)}
                    style={{ transform: 'scale(1.2)', marginTop: '4px' }}
                  />
                </div>
              </div>
            ))}

            {items.length < total && (
              <div className="mt-3 d-flex justify-content-center">
                <Button color="primary" onClick={handleLoadMore} disabled={loadMoreLoading}>
                  {loadMoreLoading ? 'Loading...' : 'Load More'}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-5 text-muted">No {entityLabel.toLowerCase()} found</div>
        )}
      </ModalBody>

      <ModalFooter className="d-flex justify-content-between align-items-center">
        <small className="text-muted">
          {selectedIds.length} selected
        </small>
        <Button
          color="primary"
          size="md"
          style={{ borderRadius: '40px', padding: '0.75rem 2rem' }}
          disabled={!selectedIds.length || submitLoading}
          onClick={handleSubmit}
        >
          {submitLoading ? <Spinner size="sm" color="light" /> : `Add ${entityLabel}`}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  totalBrands: state.brands.totalBrands,
  totalRetailers: state.retailers.totalRetailers,
});

const mapDispatchToProps = {
  getAllBrands: getAllBrandsAction,
  getAllRetailers: getAllRetailersAction,
  addSalesAgentProfile: addSalesAgentProfileAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddSalesAgentProfilesModal);
